/* ═══════════════════════════════════════════════════════
   NYC Suite: password sign-in and the authenticator code (pure logic)
   ═══════════════════════════════════════════════════════
   docs/ops/password-signin.md. No Firebase here: what a screen shows for a
   Firebase error, how a typed code is cleaned, and what the app does with a
   signed-in session (decideGate). src/lib/authFlow.js talks to Firebase;
   src/components/LoginScreen.jsx and AuthenticatorSetup.jsx draw the screens.

   The rule, the same as the Cloud Functions' (functions/src/services/
   crewAuth.ts): a password sign-in needs a verified email; and while the
   switch (Firestore authPolicy/current.passwordCode, read through the
   getAuthPolicy callable) is not "off", the 6-digit code from an
   authenticator app is needed by a password sign-in and by an event role
   (claim role rig_operator, scanner or wrangler) however it signed in,
   Google included. Google sign-ins of admins and of accounts without a role
   claim (the spare owner account) are not affected. The sign-in screen no
   longer starts Google sign-ins (2026-09-29); these rules still judge a
   Google session that was started before then.
*/

export const CODE_LENGTH = 6;
// Wrong codes in a row before the code screen sends the person back to the
// password (Firebase also slows repeated tries on its side).
export const MAX_CODE_TRIES = 3;
// What the authenticator app shows as the account's name.
export const ISSUER = "NYC Suite";

/** Digits only, at most six: "123 456" and "123-456" become "123456". */
export function cleanCode(raw) {
  return String(raw ?? "").replace(/\D/g, "").slice(0, CODE_LENGTH);
}

export function isFullCode(code) {
  return /^\d{6}$/.test(String(code ?? ""));
}

export function cleanEmail(raw) {
  return String(raw ?? "").trim();
}

export function looksLikeEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s ?? ""));
}

// The roles people hold on shared devices at events (crewAuth.ts EVENT_ROLES).
export const EVENT_ROLES = Object.freeze(["rig_operator", "scanner", "wrangler"]);

/** The switch as the app acts on it: only the exact "off" is off. */
export function normalizePolicy(value) {
  return value === "off" ? "off" : "required";
}

/** A setup key in groups of four, the way authenticator apps show them. */
export function formatKey(key) {
  return String(key ?? "").replace(/\s+/g, "").replace(/(.{4})/g, "$1 ").trim();
}

export const UNVERIFIED_EMAIL_TEXT =
  "This account's email address is not confirmed yet, so it can't sign in with a password. Ask Ivin to finish setting up your account.";
export const SIGN_IN_AGAIN_TEXT =
  "Sign in again, and enter the 6-digit code from your authenticator app when the Suite asks.";
export const ENROLLED_SIGN_IN_AGAIN_TEXT =
  "Your authenticator is set up. Sign in again and enter a code from the app.";

/**
 * What the app does with a signed-in session.
 *   facts:  { provider, role, secondFactor, emailVerified, enrolledCount }
 *           provider: the token's sign-in provider ("password", "google.com",
 *           "custom"); role: the token's role claim; secondFactor: "totp"
 *           when this sign-in passed the code.
 *   policy: "required" | "off" (only needed for a password sign-in without
 *           the code; pass null when not fetched).
 * Returns { status }:
 *   "ok"              carry on into the Suite;
 *   "needs-policy"    fetch the switch and decide again;
 *   "enroll"          show the authenticator set-up screen, nothing else;
 *   "sign-in-again"   sign out and ask for password + code (the account has
 *                     an authenticator this session did not use);
 *   "unverified"      sign out: the email is not confirmed.
 */
export function needsCodeCheck(facts) {
  if (!facts || facts.provider === "custom") return false;
  return facts.provider === "password" || EVENT_ROLES.includes(facts.role);
}

export function decideGate(facts, policy = null) {
  if (!needsCodeCheck(facts)) return { status: "ok" };
  if (facts.provider === "password" && facts.emailVerified !== true) return { status: "unverified", message: UNVERIFIED_EMAIL_TEXT };
  if (facts.secondFactor === "totp") return { status: "ok" };
  if (policy === null || policy === undefined) return { status: "needs-policy" };
  if (normalizePolicy(policy) === "off") return { status: "ok" };
  if ((facts.enrolledCount ?? 0) > 0) return { status: "sign-in-again", message: SIGN_IN_AGAIN_TEXT };
  return { status: "enroll" };
}

const NO_CONNECTION = "No connection. Check the internet and try again.";
const SWITCHED_OFF = "This account is switched off. Ask Ivin.";
const WRONG_CODE = "That code didn't match. Codes change every 30 seconds; enter the one showing now.";

// The Firebase error code (never its message), and only when it looks like one.
const withCode = (text, code) =>
  typeof code === "string" && /^[a-z]+\/[a-z0-9-]{1,60}$/.test(code) ? `${text} (${code.replace(/^auth\//, "")})` : text;

/** Email + password step. */
export function signInErrorText(code) {
  switch (code) {
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "That email and password don't match. Check them and try again. First time signing in with email? Tap Forgot password? to set your password.";
    case "auth/invalid-email":
    case "auth/missing-email":
      return "Enter your full email address.";
    case "auth/missing-password":
      return "Enter your password.";
    case "auth/too-many-requests":
      return "Too many tries. Wait a few minutes before trying again, or use Forgot password to set a new password.";
    case "auth/user-disabled":
      return SWITCHED_OFF;
    case "auth/network-request-failed":
      return NO_CONNECTION;
    case "auth/operation-not-allowed":
      return "Password sign-in isn't switched on for the Suite yet. Ask Ivin.";
    default:
      return withCode("Sign-in didn't work. Try again; if it keeps happening, ask Ivin.", code);
  }
}

/** The 6-digit code at sign-in. back: true sends the person back to the password. */
export function codeErrorText(code) {
  switch (code) {
    case "auth/invalid-verification-code":
    case "auth/invalid-credential":
      return { text: WRONG_CODE, back: false, wrongCode: true };
    case "auth/missing-code":
    case "auth/argument-error":
      return { text: "Enter the 6-digit code.", back: false };
    case "auth/too-many-requests":
      return { text: "Too many wrong codes. Wait a few minutes, then sign in again.", back: true };
    case "auth/invalid-multi-factor-session":
    case "auth/missing-multi-factor-session":
    case "auth/code-expired":
    case "auth/multi-factor-info-not-found":
      return { text: "That took too long. Sign in again.", back: true };
    case "auth/user-disabled":
      return { text: SWITCHED_OFF, back: true };
    case "auth/network-request-failed":
      return { text: NO_CONNECTION, back: false };
    default:
      return { text: withCode("The code didn't go through. Try again; if it keeps happening, ask Ivin.", code), back: false };
  }
}

/** Setting up the authenticator. signOut: true means the only way on is a fresh sign-in. */
export function enrollErrorText(code) {
  switch (code) {
    case "auth/invalid-verification-code":
    case "auth/invalid-credential":
      return { text: WRONG_CODE, signOut: false };
    case "auth/missing-code":
    case "auth/argument-error":
      return { text: "Enter the 6-digit code the app shows.", signOut: false };
    case "auth/unverified-email":
      return { text: "Your email address isn't confirmed yet, so an authenticator can't be set up. Ask Ivin to finish setting up your account.", signOut: false };
    case "auth/requires-recent-login":
    case "auth/user-token-expired":
      return { text: "For safety, sign in again to finish setting up.", signOut: true };
    case "auth/operation-not-allowed":
      return { text: "Authenticator codes aren't switched on for the Suite yet. Ask Ivin.", signOut: false };
    case "auth/maximum-second-factor-count-exceeded":
    case "auth/second-factor-already-in-use":
      return { text: "An authenticator is already set up on this account. Sign in again and enter its code; if you lost that phone, ask Ivin to reset it.", signOut: true };
    case "auth/invalid-multi-factor-session":
    case "auth/missing-multi-factor-session":
    case "auth/code-expired":
    case "auth/totp-challenge-timeout":
      return { text: "This set-up code expired. Press Start again for a new one.", signOut: false, restart: true };
    case "auth/too-many-requests":
      return { text: "Too many tries. Wait a few minutes and try again.", signOut: false };
    case "auth/user-disabled":
      return { text: SWITCHED_OFF, signOut: true };
    case "auth/network-request-failed":
      return { text: NO_CONNECTION, signOut: false };
    default:
      return { text: withCode("Setting up didn't work. Try again; if it keeps happening, ask Ivin.", code), signOut: false };
  }
}

/** Forgot password. */
export function resetErrorText(code) {
  switch (code) {
    case "auth/invalid-email":
    case "auth/missing-email":
      return "Enter your full email address.";
    case "auth/too-many-requests":
      return "Too many requests. Wait a few minutes and try again.";
    case "auth/network-request-failed":
      return NO_CONNECTION;
    default:
      return withCode("The reset email couldn't be sent. Try again; if it keeps happening, ask Ivin.", code);
  }
}
